import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { Spin, Modal, Input } from "antd";
import moment from "moment";
import { toast } from "react-toastify";
import SidebarNav from "../../sidebar";
import {
  getWalletTxByIdV2,
  manualDepositAction,
  releaseWalletTx,
  rejectWalletTx,
} from "../../../../service/api";

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  ground:  "#F8FAFC",
  surface: "#FFFFFF",
  text:    "#0F172A",
  sub:     "#64748B",
  muted:   "#94A3B8",
  border:  "#E5E7EB",
  accent:  "#3B82F6",
  amber:   "#F59E0B",
  green:   "#22C55E",
  red:     "#EF4444",
  sh:      "0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06)",
  shHover: "0 4px 12px rgba(16,24,40,.08), 0 2px 4px rgba(16,24,40,.04)",
  mono:    "'SF Mono','Cascadia Code','Fira Code',monospace",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (val, d = 8) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return val ?? "—";
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: d });
};
const fmtDate = (v) => (v ? moment(v).format("DD MMM YYYY, HH:mm:ss") : "—");

const STATUS_CFG = {
  COMPLETED: { bg: "#DCFCE7", color: "#15803D", label: "Completed",      dot: "🟢" },
  PENDING:   { bg: "#FEF3C7", color: "#B45309", label: "Pending",        dot: "🟠" },
  ON_HOLD:   { bg: "#FEF3C7", color: "#B45309", label: "On Hold",        dot: "🟡" },
  FAILED:    { bg: "#FEE2E2", color: "#B91C1C", label: "Failed",         dot: "🔴" },
  REJECTED:  { bg: "#FEE2E2", color: "#B91C1C", label: "Rejected",       dot: "🔴" },
  TRM_PROC:  { bg: "#DBEAFE", color: "#1D4ED8", label: "TRM Processing", dot: "🟡" },
};

const RISK_COLORS = { HIGH: "#EF4444", SEVERE: "#EF4444", CRITICAL: "#EF4444", MEDIUM: "#F59E0B", LOW: "#22C55E" };
const riskColor = (l) => (l ? RISK_COLORS[String(l).toUpperCase()] ?? "#F59E0B" : "#94A3B8");

// Verdict chip — coloured pill with leading status glyph (used in Compliance card).
function Chip({ tone = "neutral", children }) {
  const tones = {
    pass:    { bg: "#F0FDF4", color: "#15803D", bd: "#BBF7D0", icon: "✓" },
    warn:    { bg: "#FFFBEB", color: "#B45309", bd: "#FDE68A", icon: "⚠" },
    danger:  { bg: "#FEF2F2", color: "#B91C1C", bd: "#FECACA", icon: "✕" },
    neutral: { bg: "#F8FAFC", color: "#64748B", bd: "#E5E7EB", icon: "•" },
  };
  const s = tones[tone] ?? tones.neutral;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "6px 12px", borderRadius: 8,
      background: s.bg, color: s.color, border: `1px solid ${s.bd}`,
      fontSize: 12.5, fontWeight: 600,
    }}>
      <span style={{ fontSize: 11 }}>{s.icon}</span>
      {children}
    </span>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusPill({ status, isTrmPending }) {
  const cfg = isTrmPending ? STATUS_CFG.TRM_PROC : STATUS_CFG[status] ?? { bg: "#F1F5F9", color: "#64748B", label: status, dot: "●" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 11px", borderRadius: 20,
      background: cfg.bg, color: cfg.color,
      fontSize: 11.5, fontWeight: 700, letterSpacing: ".04em",
    }}>
      {cfg.dot} {cfg.label}
    </span>
  );
}

function TypeChip({ type }) {
  const s = type === "CREDIT"
    ? { bg: "#DCFCE7", color: "#166534" }
    : { bg: "#DBEAFE", color: "#1e40af" };
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
      textTransform: "uppercase", padding: "3px 10px", borderRadius: 5,
      background: s.bg, color: s.color,
    }}>{type}</span>
  );
}

function HashChip({ value, big = true }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span style={{ color: T.muted }}>—</span>;
  // `big` (default) = wide chip: fills the available width and shows the full value
  // left-to-right with the copy button pinned right. Pass big={false} for a compact,
  // truncated chip in tight inline contexts.
  const short = value.length > 22 ? `${value.slice(0, 10)}…${value.slice(-8)}` : value;
  const copy = (e) => {
    e.stopPropagation();
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    <span title={value} style={{
      display: big ? "flex" : "inline-flex", alignItems: "center", gap: 6,
      justifyContent: big ? "space-between" : "flex-start",
      width: big ? "100%" : undefined,
      background: "#F1F5F9", border: "1px solid #E2E8F0", borderRadius: big ? 7 : 5,
      padding: big ? "6px 10px" : "2px 8px", fontFamily: T.mono, fontSize: 11.5, color: "#334155",
    }}>
      <span style={{ overflowWrap: big ? "anywhere" : undefined, wordBreak: big ? "break-all" : undefined }}>
        {big ? value : short}
      </span>
      <button onClick={copy} style={{
        border: "none", background: "none", cursor: "pointer", flexShrink: 0,
        color: copied ? T.green : T.muted, fontSize: 11, padding: 0, lineHeight: 1,
      }}>
        {copied ? "✓" : "⧉"}
      </button>
    </span>
  );
}

function ExtLink({ href, label }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      fontSize: 12, color: T.accent, textDecoration: "none", fontWeight: 500,
    }}>
      ↗ {label || "Open"}
    </a>
  );
}

function VBadge({ type, label }) {
  const styles = {
    clean:   { bg: "#DCFCE7", color: "#166534" },
    warn:    { bg: "#FEF3C7", color: "#92400E" },
    risk:    { bg: "#FEE2E2", color: "#991B1B" },
    neutral: { bg: "#F1F5F9", color: "#64748B" },
    na:      { bg: "#F8FAFC", color: "#94A3B8" },
  };
  const s = styles[type] ?? styles.neutral;
  return (
    <span style={{
      fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em",
      textTransform: "uppercase", padding: "2px 9px", borderRadius: 4,
      background: s.bg, color: s.color, flexShrink: 0,
    }}>{label}</span>
  );
}

const VERDICT_BORDER = { clean: T.green, warn: T.amber, risk: T.red, neutral: "#CBD5E1", na: T.border };

function SectionIcon({ icon, color, bg }) {
  if (!icon) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 26, height: 26, borderRadius: 7, flexShrink: 0,
      background: bg || "#F1F5F9", color: color || T.sub,
      fontSize: 13, marginRight: 8,
    }}>{icon}</span>
  );
}

function Section({ id, title, icon, iconColor, iconBg, verdictType = "neutral", badge, children }) {
  // Per design spec: coloured left border only for alert states (risk / warn).
  const isAlert = verdictType === "risk" || verdictType === "warn";
  return (
    <div
      style={{
        background: T.surface, borderRadius: 16, boxShadow: T.sh,
        marginBottom: 16, overflow: "hidden",
        border: `1px solid ${T.border}`,
        borderLeft: isAlert ? `4px solid ${VERDICT_BORDER[verdictType]}` : `1px solid ${T.border}`,
        transition: "box-shadow .18s ease, transform .18s ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = T.shHover; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = T.sh; }}
    >
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        padding: "14px 18px",
      }}>
        <SectionIcon icon={icon} color={iconColor} bg={iconBg} />
        <div style={{ fontWeight: 700, fontSize: 14, letterSpacing: ".01em", flex: 1 }}>{title}</div>
        {badge}
      </div>
      <div style={{ borderTop: `1px solid ${T.border}`, padding: "8px 18px 16px" }}>
        {children}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 12,
      padding: "8px 0", borderBottom: `1px solid #F8FAFC`,
    }}>
      <div style={{ minWidth: 168, flexShrink: 0, fontSize: 12, color: T.sub, paddingTop: 2 }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13, wordBreak: "break-all" }}>
        {value ?? <span style={{ color: T.muted }}>—</span>}
      </div>
    </div>
  );
}

function OkTag({ children }) {
  return (
    <span style={{ background: "#DCFCE7", color: "#166534", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4 }}>
      {children}
    </span>
  );
}

function WarnTag({ children }) {
  return (
    <span style={{ background: "#FEF3C7", color: "#92400E", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4 }}>
      {children}
    </span>
  );
}

function RiskTag({ children }) {
  return (
    <span style={{ background: "#FEE2E2", color: "#991B1B", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4, marginRight: 3, marginBottom: 2, display: "inline-block" }}>
      {children}
    </span>
  );
}

// Risk score gauge (SVG)
function RiskGauge({ score }) {
  const safe = score < 30;
  const high = score >= 70;
  const color = high ? T.red : safe ? T.green : T.amber;
  const r = 14;
  const circ = 2 * Math.PI * r; // ~87.96
  const fill = (score / 100) * circ;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <svg width={52} height={52} viewBox="0 0 36 36" aria-label={`Risk score ${score} of 100`} style={{ flexShrink: 0 }}>
        <circle cx={18} cy={18} r={r} fill="none" stroke="#E2E8F0" strokeWidth={3.5} />
        <circle
          cx={18} cy={18} r={r} fill="none"
          stroke={color} strokeWidth={3.5}
          strokeDasharray={`${fill} ${circ - fill}`}
          strokeLinecap="round"
          transform="rotate(-90 18 18)"
        />
        <text x={18} y={22.5} textAnchor="middle" fontSize={9.5} fontWeight={800} fill={T.text} fontFamily="system-ui">
          {score}
        </text>
      </svg>
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color }}>{safe ? "No risk" : high ? "High risk" : "Medium risk"}</div>
        <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>out of 100</div>
      </div>
    </div>
  );
}

// Tile component for summary strip
function Tile({ label, icon, iconBg, iconColor, iconRight = false, children }) {
  const iconEl = icon && (
    <span style={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
      background: iconBg || "#F1F5F9", color: iconColor || T.sub, fontSize: 19,
    }}>{icon}</span>
  );
  return (
    <div style={{ background: T.surface, borderRadius: 16, boxShadow: T.sh, padding: "16px 18px" }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.muted, marginBottom: 8 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 13, justifyContent: iconRight ? "space-between" : "flex-start" }}>
        {!iconRight && iconEl}
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        {iconRight && iconEl}
      </div>
    </div>
  );
}

// ─── TRM sections ─────────────────────────────────────────────────────────────

function TrmAddressSection({ data }) {
  const rows = Array.isArray(data) ? data : data ? [data] : [];
  if (!rows.length) return <p style={{ color: T.muted, fontSize: 13, padding: "12px 0" }}>No address screening data available.</p>;
  const r = rows[0];
  const risk = r?.addressHighestRiskScoreLevel;
  return (
    <>
      <KV label="Address Screened" value={<HashChip value={r?.address} />} />
      <KV label="Chain" value={r?.chain} />
      <KV label="Highest Risk Level"
        value={risk
          ? <strong style={{ color: riskColor(risk) }}>{risk}</strong>
          : <span style={{ color: T.green, fontWeight: 600 }}>No risk detected</span>}
      />
      <KV label="Screened At" value={fmtDate(r?.screenedAt)} />
      <KV label="Risk Indicators"
        value={r?.addressRiskIndicators?.length
          ? r.addressRiskIndicators.map((i, idx) => <RiskTag key={idx}>{i?.category || i?.riskType || JSON.stringify(i)}</RiskTag>)
          : <OkTag>None detected</OkTag>}
      />
      <KV label="Entities"
        value={r?.entities?.length
          ? r.entities.map((e, idx) => <WarnTag key={idx}>{e?.name || JSON.stringify(e)}</WarnTag>)
          : <span style={{ color: T.muted }}>None</span>}
      />
      {r?.trmAppUrl && (
        <div style={{ paddingTop: 8 }}>
          <ExtLink href={r.trmAppUrl} label="Open in TRM Dashboard" />
        </div>
      )}
    </>
  );
}

function TrmTransferSection({ data }) {
  if (!data?.uuid) return <p style={{ color: T.muted, fontSize: 13, padding: "12px 0" }}>No TRM transfer monitoring record.</p>;
  const sc = data.screenStatus;
  const scColor = sc === "SCREENED" ? T.green : sc === "PROCESSING" ? T.amber : T.amber;
  const isApiFailure = sc === "FAILED" || sc === "ERROR";
  return (
    <>
      {isApiFailure && (
        <div style={{
          background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 7,
          padding: "10px 14px", margin: "10px 0", fontSize: 12, color: "#78350F", lineHeight: 1.5,
        }}>
          <strong>Note:</strong> TRM returned {sc}
          {data.screenStatusFailedReason ? ` (${data.screenStatusFailedReason})` : ""} — this is a TRM
          API-side issue, not a risk verdict on this transaction. No risk indicators were raised.
        </div>
      )}
      <KV label="TRM Transfer UUID" value={<HashChip value={data.uuid} />} />
      <KV label="Screen Status"
        value={
          <span>
            <strong style={{ color: scColor }}>{sc || "—"}</strong>
            {data.screenStatusFailedReason && (
              <span style={{ marginLeft: 8, fontSize: 11.5, color: T.sub }}>
                · {data.screenStatusFailedReason}
              </span>
            )}
          </span>
        }
      />
      <KV label="Risk Level"
        value={data.riskScoreLevel
          ? <strong style={{ color: riskColor(data.riskScoreLevel) }}>{data.riskScoreLevel}{data.riskScoreLevelLabel ? ` (${data.riskScoreLevelLabel})` : ""}</strong>
          : <span style={{ color: T.green, fontWeight: 600 }}>{sc === "PROCESSING" ? "Pending…" : "No adverse risk"}</span>}
      />
      <KV label="Asset / Chain" value={`${(data.asset || "—").toUpperCase()} on ${data.chain || "—"}`} />
      <KV label="Amount" value={data.assetAmount ? `${data.assetAmount} (${data.fiatCurrency} ${fmt(data.fiatValue, 2)})` : "—"} />
      <KV label="Destination" value={<HashChip value={data.destinationAddress} />} />
      <KV label="On-chain Tx Hash" value={<HashChip value={data.onchainReference} />} />
      <KV label="Transfer Type" value={data.transferType} />
      <KV label="Submitted At" value={fmtDate(data.submittedAt)} />
      <KV label="Alerts"
        value={Array.isArray(data.alerts) && data.alerts.length
          ? data.alerts.map((a, i) => <RiskTag key={i}>{a?.alertType || JSON.stringify(a)}</RiskTag>)
          : <OkTag>No alerts</OkTag>}
      />
      {data.trmAppUrl && (
        <div style={{ paddingTop: 8 }}>
          <ExtLink href={data.trmAppUrl} label="Open Transfer in TRM Dashboard" />
        </div>
      )}
    </>
  );
}

function verdictColorFor(status) {
  const s = (status || "").toUpperCase();
  if (["DECLINED", "REJECTED", "BLOCKED", "CANCELLED"].includes(s)) return T.red;
  if (["ACCEPTED", "BELOW_THRESHOLD", "NON_CUSTODIAL", "SENT"].includes(s)) return T.green;
  return T.amber;
}

function NotabeneSection({ data, summary, txType }) {
  const raw            = data?.raw ?? {};
  const isVasp         = data?.isVasp;
  const transferId     = data?.transferId ?? data?.id ?? summary?.notabene?.transferId ?? raw?.id;
  const transferStatus = data?.transferStatus ?? summary?.notabene?.verdict ?? raw?.status;
  const originatorVASP = data?.originatorVASPdid ?? raw?.originatorVASPdid;
  const beneficiaryVASP = raw?.beneficiaryVASPdid ?? data?.beneficiaryVASPdid;

  // ── Withdrawal side ─────────────────────────────────────────────────────
  if (txType === "DEBIT" || data?.validationType) {
    if (!transferId && !transferStatus) {
      return <p style={{ color: T.muted, fontSize: 13, padding: "12px 0" }}>No Notabene data for this withdrawal.</p>;
    }
    // Unhosted / self-hosted destination — Travel Rule not applicable
    if (data?.validationType === "NON_CUSTODIAL" && !data?.beneficiaryVASPdid) {
      return (
        <>
          <div style={{
            background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8,
            padding: "12px 14px", margin: "8px 0 12px", fontSize: 13, color: "#1e40af", lineHeight: 1.55,
          }}>
            <strong>Self-hosted wallet — Travel Rule not applicable.</strong> The destination address is not
            associated with a registered VASP, so no Travel Rule obligation exists for this withdrawal.
          </div>
          <KV label="Transfer ID" value={transferId ? <HashChip value={transferId} /> : <span style={{ color: T.muted }}>—</span>} />
          <KV label="Beneficiary VASP" value={<span style={{ color: T.sub }}>None — self-hosted / unhosted wallet</span>} />
          <KV label="txHash Reported"
            value={summary?.notabene?.txHashReported ? <OkTag>Yes — reported</OkTag> : <WarnTag>Not yet</WarnTag>}
          />
        </>
      );
    }
    return (
      <>
        <KV label="Transfer ID" value={<HashChip value={transferId} />} />
        <KV label="Status / Verdict" value={transferStatus ? <strong style={{ color: verdictColorFor(transferStatus) }}>{transferStatus}</strong> : "—"} />
        <KV label="Validation Type" value={data?.validationType} />
        <KV label="Beneficiary VASP" value={<HashChip value={beneficiaryVASP} />} />
        <KV label="txHash Reported"
          value={summary?.notabene?.txHashReported ? <OkTag>Yes — reported</OkTag> : <WarnTag>Not yet</WarnTag>}
        />
      </>
    );
  }

  // ── Deposit side: self-hosted wallet (no originating VASP) ──────────────
  if (isVasp === false) {
    return (
      <>
        <div style={{
          background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8,
          padding: "12px 14px", margin: "8px 0 12px", fontSize: 13, color: "#1e40af", lineHeight: 1.55,
        }}>
          <strong>Self-hosted wallet — Travel Rule not applicable.</strong> The originating address is not
          associated with a registered VASP, so no Travel Rule obligation exists for this deposit.
        </div>
        <KV label="Transfer ID (txNotify)" value={transferId ? <HashChip value={transferId} /> : <span style={{ color: T.muted }}>—</span>} />
        <KV label="Originator VASP"
          value={originatorVASP ? <HashChip value={originatorVASP} /> : <span style={{ color: T.sub }}>None — self-hosted / unhosted wallet</span>}
        />
        <KV label="Beneficiary VASP" value={beneficiaryVASP ? <HashChip value={beneficiaryVASP} /> : <span style={{ color: T.muted }}>—</span>} />
        <KV label="Status" value={transferStatus ? <strong style={{ color: verdictColorFor(transferStatus) }}>{transferStatus}</strong> : "—"} />
        {raw?.protocol && <KV label="Protocol" value={raw.protocol} />}
        {raw?.transactionAsset && <KV label="Asset" value={raw.transactionAsset} />}
      </>
    );
  }

  // ── Deposit side: VASP deposit with full data ───────────────────────────
  if (isVasp === true && transferId) {
    return (
      <>
        <KV label="Transfer ID" value={<HashChip value={transferId} />} />
        <KV label="Status / Verdict" value={transferStatus ? <strong style={{ color: verdictColorFor(transferStatus) }}>{transferStatus}</strong> : "—"} />
        <KV label="Originator VASP" value={<HashChip value={originatorVASP} />} />
        <KV label="Beneficiary VASP" value={<HashChip value={beneficiaryVASP} />} />
        {raw?.protocol && <KV label="Protocol" value={raw.protocol} />}
        {raw?.transactionAsset && <KV label="Asset" value={raw.transactionAsset} />}
        {raw?.ivms101 !== undefined && (
          <KV label="IVMS101" value={raw.ivms101 ? <OkTag>Provided</OkTag> : <WarnTag>Not provided</WarnTag>} />
        )}
      </>
    );
  }

  // ── Fallback: deposit not screened ──────────────────────────────────────
  return (
    <p style={{ color: T.sub, fontSize: 13, padding: "12px 0", lineHeight: 1.6 }}>
      Notabene screening was not triggered for this deposit (counterparty address unavailable or Notabene not configured).
    </p>
  );
}

// ─── Timeline ───────────────────────────────────────────────────────────────

function Timeline({ events }) {
  const items = events.filter((e) => e.at);
  if (!items.length) return <p style={{ color: T.muted, fontSize: 13, padding: "12px 0" }}>No timeline events recorded.</p>;
  return (
    <div style={{ padding: "8px 0 2px" }}>
      {items.map((e, i) => {
        const last = i === items.length - 1;
        const dotColor = e.tone === "danger" ? T.red : e.tone === "warn" ? T.amber : e.done ? T.green : T.accent;
        return (
          <div key={i} style={{ display: "flex", gap: 14 }}>
            {/* Rail */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
              <span style={{
                width: 13, height: 13, borderRadius: "50%", background: dotColor,
                boxShadow: `0 0 0 4px ${dotColor}1f`, marginTop: 4,
              }} />
              {!last && <span style={{ flex: 1, width: 2, background: T.border, marginTop: 4 }} />}
            </div>
            {/* Content */}
            <div style={{ paddingBottom: last ? 4 : 22, flex: 1 }}>
              <div style={{ fontSize: 11.5, fontFamily: T.mono, color: T.muted, marginBottom: 2 }}>
                {moment(e.at).format("DD MMM YYYY · HH:mm:ss")}
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{e.title}</div>
              {e.desc && <div style={{ fontSize: 12.5, color: T.sub, marginTop: 2, lineHeight: 1.5 }}>{e.desc}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// KPI stat — compact metric for the hero strip.
function Stat({ label, icon, iconBg, iconColor, primary, secondary, primaryColor }) {
  return (
    <div style={{
      background: T.surface, borderRadius: 16, boxShadow: T.sh,
      padding: "16px 18px", display: "flex", alignItems: "center", gap: 13, minWidth: 0,
    }}>
      {icon && (
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 40, height: 40, borderRadius: 11, flexShrink: 0,
          background: iconBg || "#F1F5F9", color: iconColor || T.sub, fontSize: 17,
        }}>{icon}</span>
      )}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: T.muted, marginBottom: 3 }}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: primaryColor || T.text, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{primary}</div>
        {secondary && <div style={{ fontSize: 11.5, color: T.muted, marginTop: 1 }}>{secondary}</div>}
      </div>
    </div>
  );
}

// Alert banner — rounded icon badge + title + body, matched to the card design system.
function Banner({ tone = "info", icon, title, children }) {
  const tones = {
    info:    { bg: "#EFF6FF", bd: "#BFDBFE", iconBg: "#DBEAFE", iconColor: "#2563EB", title: "#1E40AF", text: "#1E3A8A" },
    danger:  { bg: "#FEF2F2", bd: "#FECACA", iconBg: "#FEE2E2", iconColor: "#DC2626", title: "#991B1B", text: "#7F1D1D" },
    warn:    { bg: "#FFFBEB", bd: "#FDE68A", iconBg: "#FEF3C7", iconColor: "#B45309", title: "#92400E", text: "#78350F" },
    success: { bg: "#F0FDF4", bd: "#BBF7D0", iconBg: "#DCFCE7", iconColor: "#15803D", title: "#166534", text: "#14532D" },
  };
  const s = tones[tone] ?? tones.info;
  return (
    <div style={{
      background: s.bg, border: `1px solid ${s.bd}`, borderRadius: 16,
      padding: "16px 18px", marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 14,
    }}>
      <span style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 40, height: 40, borderRadius: 11, flexShrink: 0,
        background: s.iconBg, color: s.iconColor, fontSize: 18,
      }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 3, color: s.title }}>{title}</div>
        <div style={{ fontSize: 13, color: s.text, lineHeight: 1.55 }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

const WalletTxDetail = () => {
  const { id } = useParams();
  const history = useHistory();

  const [tx, setTx]               = useState(null);
  const [loading, setLoading]     = useState(true);
  const [acting, setActing]           = useState(false);
  const [manualModal, setManualModal] = useState(null);
  const [manualNote, setManualNote]   = useState("");

  const load = useCallback(() => {
    setLoading(true);
    getWalletTxByIdV2(id)
      .then((res) => {
        if (res?.status) setTx(res?.data || res?.result || null);
        else toast.error(res?.message || "Failed to load transaction");
      })
      .catch(() => toast.error("Failed to load transaction"))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleManualAction = (action) => {
    if (acting) return;
    setActing(true);

    // Withdrawals (DEBIT) use the release/reject endpoints — release submits to Fireblocks
    // (debiting the user), reject refunds. Deposits (CREDIT) use manual-deposit-action.
    const isWithdrawal = tx?.type === "DEBIT";
    let req;
    let successMsg;
    if (isWithdrawal && action === "complete") {
      req = releaseWalletTx(tx._id, { adminNote: manualNote || "Approved & debited by admin" });
      successMsg = "Withdrawal approved — submitted to Fireblocks";
    } else if (isWithdrawal && action === "reject") {
      req = rejectWalletTx(tx._id, { adminNote: manualNote || "Rejected by admin" });
      successMsg = "Withdrawal rejected and refunded";
    } else {
      const labels = {
        complete: "Deposit credited to user's wallet",
        hold:     "Deposit placed on hold for review",
        reject:   "Deposit rejected",
      };
      req = manualDepositAction(tx._id, { action, note: manualNote });
      successMsg = labels[action] || "Action applied";
    }

    req
      .then((res) => {
        if (res?.status) {
          toast.success(successMsg);
          setManualModal(null);
          setManualNote("");
          load();
        } else {
          toast.error(res?.message || "Action failed");
        }
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Action failed"))
      .finally(() => setActing(false));
  };

  const status        = tx?.status;
  const isTrmPending  = status === "PENDING" && tx?.holdSourceTag === "TRM_PROCESSING";
  // Withdrawal held purely awaiting Notabene Travel Rule clearance — auto-released by the
  // poll cron once cleared (distinct from a risk/compliance HOLD that needs admin judgement).
  const isCompliancePending = status === "ON_HOLD" &&
    (tx?.holdSourceTag === "WITHDRAW_COMPLIANCE_PENDING" || tx?.holdSourceTag === "WITHDRAW_COMPLIANCE_RELEASING");
  const screenSummary = tx?.screeningSummary ?? {};
  const riskScore     = tx?.riskScore ?? 0;
  const indicators    = tx?.riskIndicators ?? [];

  // Determine compliance summary verdict type
  const hasTrmRisk = screenSummary.trm?.verdict && screenSummary.trm.verdict !== "LOW";
  const hasNbRisk  = ["DECLINED","REJECTED","BLOCKED","CANCELLED"].includes((screenSummary.notabene?.verdict || "").toUpperCase());
  const compVt     = (hasTrmRisk || hasNbRisk) ? "risk" : riskScore > 50 ? "warn" : "clean";

  // TRM transfer section verdict
  const trmTxSc = tx?.trmTransferData?.screenStatus;
  const trmTxVt = trmTxSc === "SCREENED" ? "clean" : trmTxSc === "PROCESSING" ? "warn" : trmTxSc ? "warn" : "neutral";

  // TRM address section verdict
  const trmAddrRisk = Array.isArray(tx?.trmScreeningData) && tx.trmScreeningData.length
    ? tx.trmScreeningData[0]?.addressHighestRiskScoreLevel
    : null;
  const trmAddrVt = !tx?.trmScreeningData?.length ? "neutral" : trmAddrRisk ? "risk" : "clean";

  // Notabene section verdict type
  const nbVerdict = (screenSummary.notabene?.verdict || tx?.notabeneData?.transferStatus || "").toUpperCase();
  const nbVt = ["DECLINED", "REJECTED", "BLOCKED", "CANCELLED"].includes(nbVerdict) ? "risk"
    : ["ACCEPTED", "BELOW_THRESHOLD", "NON_CUSTODIAL", "SENT"].includes(nbVerdict) ? "clean"
    : nbVerdict ? "warn"
    : "na";

  // ── KPI hero values ──────────────────────────────────────────────────────
  const trmKpiStatus = screenSummary.trm?.screenStatus || tx?.trmTransferData?.screenStatus || null;
  const trmKpi = !trmKpiStatus ? { text: "—", color: T.muted }
    : trmKpiStatus === "PROCESSING" ? { text: "Processing", color: T.amber }
    : ["SUCCEEDED", "SCREENED"].includes(trmKpiStatus) ? { text: "Succeeded", color: T.green }
    : ["FAILED", "ERROR"].includes(trmKpiStatus) ? { text: trmKpiStatus, color: T.red }
    : { text: trmKpiStatus, color: T.sub };

  const isVaspDep = tx?.notabeneData?.isVasp;
  // Withdrawal to unhosted/self-hosted wallet — no isVasp field, use validationType instead
  const isNonCustodialWd = tx?.type === "DEBIT" && tx?.notabeneData?.validationType === "NON_CUSTODIAL" && !tx?.notabeneData?.beneficiaryVASPdid;
  const trKpi = (isVaspDep === false || isNonCustodialWd) ? { text: "Not applicable", color: T.sub }
    : nbVt === "risk" ? { text: nbVerdict || "Flagged", color: T.red }
    : nbVt === "clean" ? { text: nbVerdict || "Cleared", color: T.green }
    : nbVerdict ? { text: nbVerdict, color: T.amber }
    : { text: "—", color: T.muted };

  // ── Timeline events (deposit lifecycle) ──────────────────────────────────
  const tlTerminal =
    status === "COMPLETED" ? { title: "Deposit credited", desc: "Funds released to the user's wallet.", tone: "ok", done: true }
    : status === "REJECTED" ? { title: "Deposit rejected", desc: tx?.holdReason || "Permanently declined.", tone: "danger", done: true }
    : status === "FAILED"   ? { title: "Deposit failed", desc: tx?.holdReason || "Screening failed.", tone: "danger", done: true }
    : status === "ON_HOLD"  ? { title: "Held for review", desc: tx?.holdReason || "Awaiting admin decision.", tone: "warn", done: true }
    : { title: "Awaiting finalization", desc: "Screening in progress.", tone: "warn", done: false };
  const timelineEvents = [
    { at: tx?.createdAt, title: "Deposit detected", desc: "Inbound transfer observed on-chain.", done: true },
    { at: tx?.trmScreeningData?.[0]?.screenedAt, title: "TRM address screening", desc: trmAddrRisk ? `Risk: ${trmAddrRisk}` : "Address screened — no risk detected.", tone: trmAddrRisk ? "danger" : "ok", done: true },
    { at: tx?.trmTransferData?.submittedAt, title: "TRM transaction monitoring", desc: trmKpi.text === "Processing" ? "Submitted — awaiting screening result." : `Screen status: ${trmKpi.text}`, tone: trmKpiStatus === "PROCESSING" ? "warn" : "ok", done: true },
    { at: tx?.notabeneData?.raw?.createdAt, title: "Travel Rule check", desc: (isVaspDep === false || isNonCustodialWd) ? "Self-hosted wallet — no obligation." : trKpi.text, tone: nbVt === "risk" ? "danger" : "ok", done: true },
    { at: tx?.processedAt, ...tlTerminal },
  ];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        {/* Sticky command bar */}
        <div style={{
          position: "sticky", top: 0, zIndex: 100,
          background: T.surface, borderBottom: `1px solid ${T.border}`,
          padding: "10px 24px", display: "flex", alignItems: "center", gap: 10,
        }}>
          <button
            onClick={() => history.push("/admin/finance/funding-wallet-transactions")}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 13, fontWeight: 500, color: T.sub,
              border: `1px solid ${T.border}`, background: "none",
              borderRadius: 7, padding: "5px 12px", cursor: "pointer",
            }}
          >
            ← Back
          </button>
          <span style={{
            fontFamily: T.mono, fontSize: 11.5, color: T.sub,
            background: T.ground, padding: "4px 10px", borderRadius: 6, letterSpacing: ".02em",
          }}>
            {id ? `${id.slice(0, 8)}…${id.slice(-6)}` : "—"}
          </span>
          {tx && <StatusPill status={status} isTrmPending={isTrmPending} />}
          {tx?.type && <TypeChip type={tx.type} />}
          {tx && (tx.reviewDecision === "RELEASED" || tx.reviewDecision === "REJECTED") && (
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: ".04em",
              padding: "3px 10px", borderRadius: 20,
              background: tx.reviewDecision === "RELEASED" ? "#DCFCE7" : "#FEE2E2",
              color: tx.reviewDecision === "RELEASED" ? "#15803D" : "#B91C1C",
            }}>
              ✓ Manually {tx.reviewDecision === "RELEASED" ? "approved" : "rejected"}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <button
            onClick={load}
            disabled={loading}
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 13, fontWeight: 500, color: T.accent,
              border: "1px solid #BFDBFE", background: "#EFF6FF",
              borderRadius: 7, padding: "5px 12px", cursor: "pointer",
            }}
          >
            {loading ? "…" : "↻"} Refresh
          </button>
        </div>

        <div className="content container-fluid" style={{ background: T.ground, minHeight: "calc(100vh - 50px)" }}>
          {/* Breadcrumb */}
          <div style={{ padding: "9px 0", fontSize: 12, color: T.muted, display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
            <Link to="/admin" style={{ color: T.accent, textDecoration: "none" }}>Dashboard</Link>
            <span style={{ color: "#CBD5E1" }}>›</span>
            <Link to="/admin/finance/funding-wallet-transactions" style={{ color: T.accent, textDecoration: "none" }}>
              Crypto Wallet Transactions
            </Link>
            <span style={{ color: "#CBD5E1" }}>›</span>
            <span>Detail</span>
          </div>

          {loading && (
            <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
              <Spin size="large" />
            </div>
          )}

          {!loading && !tx && (
            <div style={{ textAlign: "center", padding: 80, color: T.sub }}>Transaction not found.</div>
          )}

          {!loading && tx && (
            <div style={{ maxWidth: 1440, margin: "0 auto" }}>

              {/* ── TRM Processing banner ──────────────────────── */}
              {isTrmPending && (
                <Banner tone="info" icon="↻" title="Awaiting TRM screening">
                  TRM is processing this transaction asynchronously. The deposit will{" "}
                  <strong>automatically proceed</strong> once TRM returns a clean verdict, or be placed on hold
                  if TRM identifies risk — typically within 10 minutes. No action needed.
                </Banner>
              )}

              {/* ── TRM Failed banner — CREDIT only ───────────── */}
              {status === "FAILED" && tx?.type === "CREDIT" && (
                <Banner tone="danger" icon="✕" title="TRM screening failed — deposit not credited">
                  TRM returned a <strong>FAILED</strong> status
                  {tx.screeningSummary?.trm?.screenStatusFailedReason
                    ? <> (<code style={{ fontSize: 11.5 }}>{tx.screeningSummary.trm.screenStatusFailedReason}</code>)</>
                    : null
                  } — this is a TRM API data issue, not a risk signal. No funds have been credited.
                  If you have manually verified this transaction, you can approve and credit the deposit.
                  <div style={{ marginTop: 12 }}>
                    <button
                      onClick={() => { setManualNote(""); setManualModal("complete"); }}
                      style={{
                        background: T.green, color: "#fff", border: "none",
                        borderRadius: 8, padding: "8px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer",
                      }}
                    >
                      Approve &amp; credit deposit
                    </button>
                  </div>
                </Banner>
              )}

              {/* ── Travel Rule compliance-pending banner (withdrawals) ── */}
              {isCompliancePending && (
                <Banner tone="info" icon="↻" title="Completing Travel Rule compliance">
                  This withdrawal passed TRM wallet screening and is <strong>awaiting Notabene Travel Rule
                  clearance</strong> from the beneficiary VASP. The reserved funds are held and it will be{" "}
                  <strong>submitted to Fireblocks automatically</strong> once Notabene clears — typically within
                  a few minutes. You can override and send/reject now if needed.
                  {tx.holdSourceTag === "WITHDRAW_COMPLIANCE_RELEASING" && (
                    <div style={{ marginTop: 6 }}><strong>Status:</strong> releasing to Fireblocks…</div>
                  )}
                  <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      onClick={() => { setManualNote(""); setManualModal("complete"); }}
                      style={{
                        background: T.green, color: "#fff", border: "none",
                        borderRadius: 8, padding: "8px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer",
                      }}
                    >
                      Override — approve &amp; debit now
                    </button>
                    <button
                      onClick={() => { setManualNote(""); setManualModal("reject"); }}
                      style={{
                        background: "#fff", color: T.red, border: `1px solid ${T.red}`,
                        borderRadius: 8, padding: "8px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer",
                      }}
                    >
                      Reject &amp; refund
                    </button>
                  </div>
                </Banner>
              )}

              {/* ── On Hold banner — admin can approve/credit or reject ── */}
              {status === "ON_HOLD" && !isCompliancePending && (
                <Banner tone="warn" icon="⚠" title="Transaction held for review">
                  <div><strong>Reason:</strong> {tx.holdReason || "No reason specified"}</div>
                  {tx.holdSourceTag && <div style={{ marginTop: 2 }}><strong>Flagged by:</strong> {tx.holdSourceTag}</div>}
                  <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      onClick={() => { setManualNote(""); setManualModal("complete"); }}
                      style={{
                        background: T.green, color: "#fff", border: "none",
                        borderRadius: 8, padding: "8px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer",
                      }}
                    >
                      {tx.type === "DEBIT" ? "Approve & debit withdrawal" : "Approve & credit deposit"}
                    </button>
                    <button
                      onClick={() => { setManualNote(""); setManualModal("reject"); }}
                      style={{
                        background: "#fff", color: T.red, border: `1px solid ${T.red}`,
                        borderRadius: 8, padding: "8px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer",
                      }}
                    >
                      {tx.type === "DEBIT" ? "Reject & refund" : "Reject deposit"}
                    </button>
                  </div>
                </Banner>
              )}

              {/* ── Manually approved banner ───────────────────── */}
              {tx.reviewDecision === "RELEASED" && (
                <Banner tone="success" icon="✓" title={tx.type === "DEBIT" ? "Manually approved & sent by admin" : "Manually approved & credited by admin"}>
                  {tx.type === "DEBIT"
                    ? "This withdrawal was approved by an admin after manual review and submitted to Fireblocks for on-chain transfer."
                    : "This deposit was credited to the user's wallet by an admin after manual review — it was not auto-approved by the screening pipeline."}
                  <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: "4px 24px", fontSize: 12.5 }}>
                    {(tx.reviewedByName || tx.reviewedByEmail || tx.reviewedBy) && (
                      <span>
                        <strong>Reviewed by:</strong>{" "}
                        {tx.reviewedByName || tx.reviewedByEmail || String(tx.reviewedBy)}
                        {tx.reviewedByName && tx.reviewedByEmail && (
                          <span style={{ color: T.muted }}> ({tx.reviewedByEmail})</span>
                        )}
                      </span>
                    )}
                    {tx.reviewedAt && <span><strong>Reviewed at:</strong> {fmtDate(tx.reviewedAt)}</span>}
                  </div>
                  {tx.adminNote && <div style={{ marginTop: 6 }}><strong>Note:</strong> {tx.adminNote}</div>}
                </Banner>
              )}

              {/* ── Rejected banner — admin can override & credit ── */}
              {status === "REJECTED" && (
                <Banner tone="danger" icon="✕" title={tx.reviewDecision === "REJECTED" ? "Rejected by admin" : "Auto-rejected by screening pipeline"}>
                  <div><strong>Reason:</strong> {tx.holdReason || "No reason specified"}</div>
                  {tx.holdSourceTag && <div style={{ marginTop: 2 }}><strong>Flagged by:</strong> {tx.holdSourceTag}</div>}
                  {(tx.reviewedByName || tx.reviewedByEmail || tx.reviewedBy) && (
                    <div style={{ marginTop: 2 }}>
                      <strong>Reviewed by:</strong> {tx.reviewedByName || tx.reviewedByEmail || String(tx.reviewedBy)}
                      {tx.reviewedAt && <span style={{ color: T.muted }}> · {fmtDate(tx.reviewedAt)}</span>}
                    </div>
                  )}
                  {tx.adminNote && <div style={{ marginTop: 2 }}><strong>Note:</strong> {tx.adminNote}</div>}
                  <div style={{ marginTop: 12 }}>
                    <button
                      onClick={() => { setManualNote(""); setManualModal("complete"); }}
                      style={{
                        background: T.green, color: "#fff", border: "none",
                        borderRadius: 8, padding: "8px 18px", fontWeight: 600, fontSize: 13, cursor: "pointer",
                      }}
                    >
                      {tx.type === "DEBIT" ? "Override — approve & debit withdrawal" : "Override — approve & credit deposit"}
                    </button>
                  </div>
                </Banner>
              )}

              {/* ── Withdrawal failed (Fireblocks) banner ──────── */}
              {status === "FAILED" && tx.type === "DEBIT" && (
                <Banner tone="danger" icon="✕" title="Withdrawal failed at Fireblocks">
                  <div><strong>Reason:</strong> {tx.errorMessage || tx.fireblocksSubStatus || tx.holdReason || "No reason reported by Fireblocks"}</div>
                  <div style={{ marginTop: 6 }}>
                    The reserved amount has been <strong>refunded</strong> to the user's funding wallet.
                    The user can submit a new withdrawal.
                  </div>
                </Banner>
              )}

              {/* ── KPI hero strip ─────────────────────────────── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, marginBottom: 16 }}>
                <Stat
                  label="Amount" icon="◆" iconBg="#EDE9FE" iconColor="#7C3AED"
                  primary={`${fmt(tx.amount, 8)} ${tx.coinSymbol}`}
                  secondary={tx.amountUsd ? `≈ $${fmt(tx.amountUsd, 2)} USD` : tx.chain}
                />
                <Stat
                  label="Network" icon="🔗" iconBg="#CFFAFE" iconColor="#0891B2"
                  primary={tx.chain || "—"}
                  secondary={tx.assetId}
                />
                <Tile label="Risk Score">
                  <RiskGauge score={riskScore} />
                </Tile>
                <Stat
                  label="TRM Screening"
                  icon={trmKpi.color === T.green ? "✓" : trmKpi.color === T.red ? "🛡" : "🔍"}
                  iconBg={trmKpi.color === T.green ? "#DCFCE7" : trmKpi.color === T.red ? "#FEE2E2" : "#FEF3C7"}
                  iconColor={trmKpi.color}
                  primary={trmKpi.text} primaryColor={trmKpi.color}
                  secondary={trmAddrRisk ? `Address: ${trmAddrRisk}` : tx.trmScreeningData?.length ? "Address clean" : "Transaction monitoring"}
                />
                <Stat
                  label="Travel Rule"
                  icon={trKpi.color === T.green ? "✓" : trKpi.color === T.red ? "⚠" : "🌐"}
                  iconBg={trKpi.color === T.green ? "#DCFCE7" : trKpi.color === T.red ? "#FEE2E2" : "#F1F5F9"}
                  iconColor={trKpi.color}
                  primary={trKpi.text} primaryColor={trKpi.color}
                  secondary={(isVaspDep === false || isNonCustodialWd) ? "Self-hosted wallet" : "Notabene"}
                />
                <Stat
                  label="Received" icon="🗓" iconBg="#EDE9FE" iconColor="#7C3AED"
                  primary={tx.createdAt ? moment(tx.createdAt).format("DD MMM YYYY") : "—"}
                  secondary={tx.createdAt ? moment(tx.createdAt).format("HH:mm:ss") : null}
                />
              </div>

              {/* ── Two-column detail grid (equal height) ──────── */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                gap: 12,
                marginBottom: 4,
                alignItems: "stretch",
              }}>
                {/* Left column — Transaction Info */}
                <Section
                  title="Transaction Information"
                  icon="🗒" iconColor="#2563EB" iconBg="#DBEAFE"
                  verdictType="neutral"
                >
                  <KV label="Transaction ID" value={<HashChip value={String(tx._id)} />} />
                  <KV label="Status" value={<StatusPill status={status} isTrmPending={isTrmPending} />} />
                  <KV label="Type" value={<TypeChip type={tx.type} />} />
                  <KV label="Coin / Asset"
                    value={<span><strong>{tx.coinSymbol}</strong> <span style={{ color: T.muted, fontSize: 12 }}>({tx.assetId})</span></span>}
                  />
                  <KV label="Amount" value={<span style={{ fontSize: 19, fontWeight: 800, letterSpacing: "-.01em" }}>{fmt(tx.amount, 8)} {tx.coinSymbol}</span>} />
                  <KV label="Chain" value={tx.chain} />
                  <KV label="User ID" value={<HashChip value={String(tx.userId)} />} />
                  <KV label="Created At" value={fmtDate(tx.createdAt)} />
                  {tx.processedAt && <KV label="Processed At" value={fmtDate(tx.processedAt)} />}
                  {tx.reviewedAt  && <KV label="Reviewed At"  value={fmtDate(tx.reviewedAt)} />}
                  {tx.reviewedBy  && <KV label="Reviewed By"  value={tx.reviewedByName || tx.reviewedByEmail || String(tx.reviewedBy)} />}
                  {tx.adminNote   && <KV label="Admin Note"   value={tx.adminNote} />}
                  {tx.holdReason && status !== "COMPLETED" && (
                    <KV
                      label={status === "FAILED" ? "Failure Reason" : "Hold Reason"}
                      value={<span style={{ color: status === "FAILED" ? T.red : T.amber, fontWeight: 500 }}>{tx.holdReason}</span>}
                    />
                  )}
                  {tx.holdSourceTag && status !== "COMPLETED" && status !== "FAILED" && (
                    <KV label="Hold Source" value={<WarnTag>{tx.holdSourceTag}</WarnTag>} />
                  )}
                </Section>

                {/* Right column — Compliance Summary */}
                <Section
                    title="Compliance Summary"
                    icon="🛡" iconColor={compVt === "clean" ? "#16A34A" : compVt === "risk" ? "#DC2626" : "#D97706"}
                    iconBg={compVt === "clean" ? "#DCFCE7" : compVt === "risk" ? "#FEE2E2" : "#FEF3C7"}
                    verdictType={compVt}
                    badge={<VBadge type={compVt} label={compVt === "clean" ? "All clear" : compVt === "risk" ? "Flagged" : "Review"} />}
                  >
                    {/* Verdict chips */}
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "4px 0 12px" }}>
                      <Chip tone={riskScore >= 70 ? "danger" : riskScore >= 30 ? "warn" : "pass"}>
                        {riskScore >= 70 ? "High risk" : riskScore >= 30 ? "Medium risk" : "Low risk"}
                      </Chip>
                      <Chip tone={hasTrmRisk ? "danger" : trmKpiStatus === "PROCESSING" ? "warn" : "pass"}>
                        {hasTrmRisk ? "TRM flagged" : trmKpiStatus === "PROCESSING" ? "TRM pending" : "TRM passed"}
                      </Chip>
                      <Chip tone={(isVaspDep === false || isNonCustodialWd) ? "neutral" : nbVt === "risk" ? "danger" : nbVt === "clean" ? "pass" : "warn"}>
                        {(isVaspDep === false || isNonCustodialWd) ? "Travel Rule N/A" : nbVt === "risk" ? "Travel Rule flagged" : nbVt === "clean" ? "Travel Rule cleared" : "Travel Rule incomplete"}
                      </Chip>
                      <Chip tone={screenSummary.fireblocks?.amlVerdict ? "danger" : "pass"}>
                        {screenSummary.fireblocks?.amlVerdict ? "AML flagged" : "AML clean"}
                      </Chip>
                    </div>
                    <KV label="Overall Risk Score"
                      value={
                        <span>
                          <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-.01em", color: riskScore >= 70 ? T.red : riskScore >= 30 ? T.amber : T.green }}>
                            {riskScore}
                          </span>
                          <span style={{ fontSize: 12, color: T.muted }}> / 100</span>
                        </span>
                      }
                    />
                    <KV label="Risk Indicators"
                      value={indicators.length ? indicators.map((i, idx) => <RiskTag key={idx}>{i}</RiskTag>) : <OkTag>None</OkTag>}
                    />
                    {Array.isArray(tx.matchedIndicators) && tx.matchedIndicators.length > 0 && (
                      <KV label="Matched (weighted)"
                        value={
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                            {tx.matchedIndicators
                              .slice()
                              .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
                              .map((m, idx) => {
                                const c = m.action === "REJECT" ? T.red : m.action === "HOLD" ? T.amber : T.muted;
                                const human = String(m.key || "").replace(/_/g, " ");
                                return (
                                  <span key={idx} title={`${m.dimension} · ${m.source}${m.riskType ? " · " + m.riskType : ""}`}
                                    style={{
                                      display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600,
                                      padding: "2px 8px", borderRadius: 6, color: c,
                                      background: `${c}14`, border: `1px solid ${c}40`, textTransform: "capitalize",
                                    }}>
                                    {human}
                                    <span style={{ opacity: 0.7, textTransform: "none" }}>· {m.action?.toLowerCase()} · w{m.weight}</span>
                                  </span>
                                );
                              })}
                          </div>
                        }
                      />
                    )}
                    <KV label="TRM Verdict"
                      value={screenSummary.trm?.verdict
                        ? <strong style={{ color: riskColor(screenSummary.trm.verdict) }}>{screenSummary.trm.verdict}</strong>
                        : <span style={{ color: T.green, fontWeight: 600 }}>No adverse finding</span>}
                    />
                    <KV label="TRM Transfer ID" value={<HashChip value={screenSummary.trm?.transferId} />} />
                    <KV label="TRM Screen Status"
                      value={
                        screenSummary.trm?.screenStatus
                          ? <span>
                              <span style={{ fontWeight: 600 }}>{screenSummary.trm.screenStatus}</span>
                              {screenSummary.trm.screenStatusFailedReason && (
                                <span style={{ marginLeft: 8, fontSize: 11.5, color: T.sub }}>
                                  · {screenSummary.trm.screenStatusFailedReason}
                                </span>
                              )}
                            </span>
                          : "—"
                      }
                    />
                    <KV label="Notabene"
                      value={screenSummary.notabene?.verdict
                        ? <WarnTag>{screenSummary.notabene.verdict}</WarnTag>
                        : <span style={{ color: T.muted }}>Not applicable — deposit</span>}
                    />
                    <KV label="Fireblocks AML"
                      value={screenSummary.fireblocks?.amlVerdict
                        ? <strong style={{ color: T.red }}>{screenSummary.fireblocks.amlVerdict}</strong>
                        : <span style={{ color: T.green, fontWeight: 600 }}>No adverse finding</span>}
                    />
                </Section>
              </div>

              {/* ── On-Chain Details (full width) ──────────────── */}
              <Section
                title="On-Chain Details"
                icon="🔗" iconColor="#0891B2" iconBg="#CFFAFE"
                verdictType="neutral"
              >
                {/* Two centered 50% columns. Left: From Address → Fireblocks ID ·
                    Right: To Address → Tx Hash. Larger address chips for readability. */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 48, rowGap: 16, paddingTop: 6 }}>
                  {[
                    ["From Address",  tx.fromAddress],
                    ["To Address",    tx.toAddress],
                    ["Fireblocks ID", tx.fireblocksId],
                    ["Tx Hash",       tx.txHash],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: "flex", alignItems: "center", gap: 14 }}>
                      <span style={{ minWidth: 104, fontSize: 12.5, color: T.sub, flexShrink: 0 }}>{label}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <HashChip value={val} big />
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* ── TRM Address Screening ──────────────────────── */}
              <Section
                title="TRM Address Screening"
                icon="🛡" iconColor={trmAddrVt === "clean" ? "#16A34A" : trmAddrVt === "risk" ? "#DC2626" : "#64748B"}
                iconBg={trmAddrVt === "clean" ? "#DCFCE7" : trmAddrVt === "risk" ? "#FEE2E2" : "#F1F5F9"}
                verdictType={trmAddrVt}
                badge={
                  <VBadge
                    type={trmAddrVt}
                    label={trmAddrVt === "clean" ? "Completed" : trmAddrVt === "risk" ? "Flagged" : "Not run"}
                  />
                }
                defaultOpen={trmAddrVt !== "neutral"}
              >
                <TrmAddressSection data={tx.trmScreeningData} />
              </Section>

              {/* ── TRM Transfer Monitoring ────────────────────── */}
              <Section
                title="TRM Transaction Monitoring"
                icon="◉"
                iconColor={trmTxSc === "SCREENED" ? "#16A34A" : trmTxSc === "PROCESSING" ? "#D97706" : trmTxSc === "FAILED" ? "#DC2626" : "#64748B"}
                iconBg={trmTxSc === "SCREENED" ? "#DCFCE7" : trmTxSc === "PROCESSING" ? "#FEF3C7" : trmTxSc === "FAILED" ? "#FEE2E2" : "#F1F5F9"}
                verdictType={trmTxVt}
                badge={
                  <VBadge
                    type={trmTxVt}
                    label={trmTxSc === "SCREENED" ? "Screened" : trmTxSc === "PROCESSING" ? "Processing…" : trmTxSc || "Not run"}
                  />
                }
                defaultOpen={!!tx.trmTransferData?.uuid}
              >
                <TrmTransferSection data={tx.trmTransferData} />
              </Section>

              {/* ── Travel Rule + Fireblocks AML (two-column) ──── */}
              <div style={{
                display: "grid",
                gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                gap: 12, alignItems: "start",
              }}>
                {/* ── Notabene / Travel Rule ──────────────────── */}
                <Section
                  title="Travel Rule"
                  icon="🌐"
                  iconColor={(tx.notabeneData?.isVasp === false || isNonCustodialWd) ? "#3B82F6" : "#64748B"}
                  iconBg={(tx.notabeneData?.isVasp === false || isNonCustodialWd) ? "#DBEAFE" : "#F1F5F9"}
                  verdictType={(tx.notabeneData?.isVasp === false || isNonCustodialWd) ? "neutral" : nbVt}
                  badge={
                    <VBadge
                      type={
                        tx.notabeneData?.isVasp === false ? "neutral"
                        : isNonCustodialWd ? "neutral"
                        : tx.notabeneData?.isVasp === true ? nbVt
                        : screenSummary.notabene?.verdict ? "neutral"
                        : "na"
                      }
                      label={
                        tx.notabeneData?.isVasp === false ? "Non-VASP deposit"
                        : isNonCustodialWd ? "Non-custodial"
                        : tx.notabeneData?.isVasp === true ? (screenSummary.notabene?.verdict || tx.notabeneData?.transferStatus || "VASP")
                        : screenSummary.notabene?.verdict || "Not screened"
                      }
                    />
                  }
                >
                  <NotabeneSection data={tx.notabeneData} summary={screenSummary} txType={tx.type} />
                </Section>

                {/* ── Fireblocks AML ──────────────────────────── */}
                <Section
                  title="Fireblocks AML"
                  icon="🧾" iconColor="#64748B" iconBg="#F1F5F9"
                  verdictType="na"
                  badge={<VBadge type={tx.fireblocksAmlData && Object.keys(tx.fireblocksAmlData).length > 0 ? "neutral" : "na"} label={tx.fireblocksAmlData && Object.keys(tx.fireblocksAmlData).length > 0 ? "Has data" : "No data"} />}
                >
                  {tx.fireblocksAmlData && Object.keys(tx.fireblocksAmlData).length > 0 ? (
                    Object.entries(tx.fireblocksAmlData).map(([k, v]) => (
                      <KV key={k} label={k} value={typeof v === "object" ? JSON.stringify(v) : String(v ?? "—")} />
                    ))
                  ) : (
                    <div style={{ padding: "8px 0 2px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                        <Chip tone="pass">No sanctions</Chip>
                        <Chip tone="pass">No watchlists</Chip>
                        <Chip tone="pass">No adverse media</Chip>
                      </div>
                      <p style={{ color: T.sub, fontSize: 13, margin: 0, lineHeight: 1.6 }}>
                        No Fireblocks AML screening record is attached to this transaction. Deposits are screened
                        through TRM; Fireblocks AML data appears here when available from the vault provider.
                      </p>
                    </div>
                  )}
                </Section>
              </div>

              {/* ── Lifecycle Timeline (full width) ────────────── */}
              <Section
                title="Lifecycle Timeline"
                icon="🕒" iconColor="#7C3AED" iconBg="#EDE9FE"
                verdictType="neutral"
              >
                <Timeline events={timelineEvents} />
              </Section>

            </div>
          )}
        </div>
      </div>

      {/* ── Manual deposit action modal ────────────────────────────── */}
      <Modal
        open={!!manualModal}
        onCancel={() => { setManualModal(null); setManualNote(""); }}
        onOk={() => handleManualAction(manualModal)}
        okText={
          manualModal === "complete" ? (tx?.type === "DEBIT" ? "Confirm — approve & debit" : "Confirm — credit deposit") :
          manualModal === "hold"     ? "Confirm — hold for review" :
                                       "Confirm — reject"
        }
        okButtonProps={{ danger: manualModal === "reject", loading: acting }}
        title={
          manualModal === "complete" ? (tx?.type === "DEBIT" ? "Approve & debit withdrawal" : "Force complete deposit") :
          manualModal === "hold"     ? "Hold for manual review" :
                                       (tx?.type === "DEBIT" ? "Reject & refund withdrawal" : "Reject deposit")
        }
        centered
        width={460}
      >
        <div style={{
          background: manualModal === "complete" ? "#F0FDF4" : manualModal === "hold" ? "#FFFBEB" : "#FFF2F0",
          border: `1px solid ${manualModal === "complete" ? "#BBF7D0" : manualModal === "hold" ? "#FDE68A" : "#FFCCC7"}`,
          borderRadius: 8, padding: "12px 14px", marginBottom: 14, fontSize: 13, lineHeight: 1.5,
          color: manualModal === "complete" ? "#166534" : manualModal === "hold" ? "#92400E" : "#991B1B",
        }}>
          {manualModal === "complete" && (tx?.type === "DEBIT"
            ? "The withdrawal will be submitted to Fireblocks for on-chain transfer to the destination address. The user's reserved balance will be debited. This cannot be undone."
            : "The deposit will be immediately credited to the user's wallet. This action cannot be undone.")}
          {manualModal === "hold"     && "The deposit will be placed on hold. You can release or reject it from the On Hold queue."}
          {manualModal === "reject"   && (tx?.type === "DEBIT"
            ? "The withdrawal will be rejected and the reserved amount refunded to the user's funding wallet."
            : "The deposit will be permanently rejected. The user will NOT receive the funds.")}
        </div>
        <div style={{ marginBottom: 8, fontWeight: 600, color: T.sub, fontSize: 13 }}>
          Admin note <span style={{ fontWeight: 400, color: T.muted }}>(optional — saved for audit trail)</span>
        </div>
        <Input.TextArea
          rows={3}
          placeholder={
            manualModal === "complete" ? "e.g. TRM API issue confirmed — transaction manually verified, safe to credit" :
            manualModal === "hold"     ? "e.g. Flagged for additional review — pending compliance sign-off" :
                                         "e.g. Rejected — TRM failure unresolvable, funds to be returned"
          }
          value={manualNote}
          onChange={(e) => setManualNote(e.target.value)}
        />
      </Modal>

    </>
  );
};

export default WalletTxDetail;
